import type { Client, StompHeaders } from '@stomp/stompjs';
import { camelCase, has, mapKeys, set } from 'lodash';
import type { Observer } from 'rxjs';
import { Observable } from 'rxjs';
import { filter, finalize, share, switchMap } from 'rxjs/operators';
import type { Primitive } from 'utility-types';

import { State, connect } from '@swf/core/api/connect';
import type { Message } from '@swf/core/types/Notification';
import { integrationTesting } from '@swf/core/utils';
import { tryParse } from '@swf/core/utils/api';

export type { Message } from '@swf/core/types/Notification';

const subscriptions: Record<string, Observable<Message>> = {};

const subscription = (client: Client, destination: string, headers: Message['headers']) =>
  new Observable((messages: Observer<Message>) => {
    const stompSub = client.subscribe(
      destination,
      (message) =>
        messages.next({
          headers: Object.keys(message.headers).reduce((memo, headerName) => {
            memo[camelCase(headerName)] = tryParse(message.headers[headerName]);
            return memo;
          }, {} as Record<string, Primitive>),
          body: tryParse(message.body)
        }),
      mapKeys({ ...(headers as StompHeaders) }, (value, key) => key.toLowerCase())
    );

    if (integrationTesting) {
      set(window, ['__STARS_SUBSCRIPTIONS__', destination], stompSub.id);
    }

    return () => {
      if (client.connected && has(client, `subscriptions.${stompSub.id}`)) {
        stompSub.unsubscribe();
      }
    };
  });

export const subscribe = <T = Message>(
  destination: string,
  headers: Message['headers'] = {},
  connection: ReturnType<typeof connect> = connect()
): Observable<T> => {
  if (!subscriptions[destination]) {
    subscriptions[destination] = connection.pipe(
      filter(({ state }) => state === State.CONNECTED),
      switchMap(({ client }) => subscription(client as Client, destination, headers)),
      finalize(() => delete subscriptions[destination]),
      share()
    );
  }

  return subscriptions[destination] as Observable<T>;
};
